import React from 'react';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { AxiosError } from 'axios';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { useGetWorkingHours } from 'src/features/workingHours/api/useGetWorkingHours';
import { LIST_WORKING_HOURS_RESOURCE } from 'src/features/workingHours/api/useListWorkingHours';
import WorkingHoursForm from 'src/features/workingHours/components/form/WorkingHoursForm';
import {
    customWorkingHoursFormSchema,
    CustomWorkingHoursFormSchemaValues,
    WorkingHoursFormSchemaValues,
} from 'src/features/workingHours/utils/formSchema';
import { workingHoursMessages } from 'src/features/workingHours/utils/messages';
import { WorkingHours } from 'src/features/workingHours/utils/types';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { useParking } from 'src/providers/ParkingProvider';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { SmartParkError } from 'src/registry/types/axios';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { useOnError } from 'src/utils/errors';

interface WorkingHoursFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedWorkingHours?: WorkingHours;
    children?: React.ReactNode;
    isOpen?: boolean;
    isPending: boolean;
    icon: IconProp;
    title: string;
    subtitle: string;
    mutate: (
        data: WorkingHoursFormSchemaValues[],
        onSuccess: () => void,
        onError: (error: AxiosError<SmartParkError, any>) => void,
    ) => void;
}

function WorkingHoursFormModal<TData>({
    selectedWorkingHours,
    className,
    isPending,
    children,
    icon,
    title,
    subtitle,
    mutate,
}: WorkingHoursFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    // We are in edit mode if we have a selected working hours
    // otherwise it means we are creating a new working hours
    const { siteId = '' } = useParams();
    const { parkingId } = useParking();
    const { formatMessage } = useIntl();

    const { data: workingHours } = useGetWorkingHours({
        hoursId: selectedWorkingHours?.id ?? '',
        siteId,
        parkingId,
        // We only want to fetch if we're in edit
        config: { enabled: !!selectedWorkingHours?.id && isModalOpen },
    });
    const onError = useOnError();

    const defaultValues = React.useMemo(
        () =>
            workingHours
                ? {
                      ...workingHours,
                      zone: workingHours.zone.id,
                      day_of_week: [workingHours.day_of_week],
                  }
                : {
                      all_day: true,
                      start_time: null,
                      end_time: null,
                      day_of_week: undefined,
                      zone: '',
                  },
        [workingHours],
    );

    const form = useForm<CustomWorkingHoursFormSchemaValues>({
        resolver: zodResolver(customWorkingHoursFormSchema),
        defaultValues,
    });

    React.useEffect(() => {
        form.reset(defaultValues);
    }, [workingHours, isModalOpen, selectedWorkingHours, defaultValues, form]);
    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) form.reset();

        setIsModalOpen(isOpen);
    };

    const onSubmit = React.useCallback(
        (values: CustomWorkingHoursFormSchemaValues) => {
            const workingHoursShapeData = values.day_of_week.map((day_of_week) => ({
                ...values,
                day_of_week,
            }));

            mutate(
                workingHoursShapeData,
                () => {
                    setIsModalOpen(false);
                    if (selectedWorkingHours) return;
                    void queryClient.invalidateQueries({ queryKey: [LIST_WORKING_HOURS_RESOURCE] });

                    toast({
                        title: formatMessage(workingHoursMessages.createWorkingHoursSuccess),
                        description: formatMessage(workingHoursMessages.createWorkingHoursSuccessDescription),
                    });
                },
                (error) => onError(error, form.setError),
            );
        },
        [mutate, selectedWorkingHours, formatMessage, onError, form.setError],
    );

    return (
        <Dialog modal open={isModalOpen} onOpenChange={onOpenChange}>
            {children ? (
                <DialogTrigger asChild>{children}</DialogTrigger>
            ) : (
                <DialogTrigger asChild>
                    <Button className={className}>
                        <FontAwesomeIcon icon={ICON_MAP.ADD} />
                        {title}
                    </Button>
                </DialogTrigger>
            )}
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={icon} />
                        <div>
                            <DialogTitle>{title}</DialogTitle>
                            <DialogDescription>{subtitle}</DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <WorkingHoursForm form={form} onSubmit={onSubmit} isCreate={!selectedWorkingHours} />
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage
                                id={generalMessages.cancel.id}
                                defaultMessage={generalMessages.cancel.defaultMessage}
                            />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="default"
                        disabled={isPending || (selectedWorkingHours && !form.formState.isDirty)}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPending && <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />}
                        {title}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default WorkingHoursFormModal;
